import { icons } from './icons.ts';
import './app-icon.ts';
import { injectAesthetic } from './shared-styles.ts';

export class AppButton extends HTMLElement {
    static get observedAttributes() {
        return ['variant', 'size', 'icon', 'disabled', 'loading', 'type', 'title'];
    }

    private seed: number;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.seed = Math.floor(Math.random() * 10000);
    }

    connectedCallback() {
        if (!this.shadowRoot?.innerHTML) {
            this.initRender();
        }
        this.updateRender();
        this.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
    }

    handleClick = (e: Event) => {
        if (this.hasAttribute('disabled') || this.hasAttribute('loading')) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        if (this.getAttribute('type') === 'submit') {
            const form = this.closest('form');
            if (form) {
                // Trigger form validation and submission
                form.requestSubmit();
            }
        }
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue !== newValue) {
            this.updateRender();
        }
    }

    private initRender() {
        if (!this.shadowRoot) return;
        this.shadowRoot.innerHTML = `
            ${injectAesthetic(this.seed)}
            <style>
                :host {
                    display: inline-block;
                    vertical-align: middle;
                    color: white;
                }

                .btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    font-family: var(--font-main);
                    font-weight: 700;
                    border-radius: var(--radius-sm);
                    cursor: pointer;
                    transition: all 0.2s;
                    border: 1.5px solid var(--border);
                    width: 100%;
                    white-space: nowrap;
                    color: inherit;
                    position: relative;
                }

                /* Sizes */
                .btn.xs { padding: 0.25rem 0.5rem; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em; gap: 0.25rem; min-height: 32px; }
                .btn.sm { padding: 0.5rem 1rem; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.05em; min-height: 40px; }
                .btn.md { padding: 0.75rem 1.75rem; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 0.05em; min-height: 48px; }
                .btn.lg { padding: 1rem 2.25rem; font-size: 1rem; text-transform: uppercase; letter-spacing: 0.05em; min-height: 56px; }

                .btn.icon-only {
                    padding: 0;
                    aspect-ratio: 1 / 1;
                    width: auto;
                    min-width: 40px;
                    height: 40px;
                    --border-sketch: rgba(255, 255, 255, 0.8);
                    overflow: visible;
                }
                .btn.icon-only.sm { min-width: 40px; height: 40px; }
                .btn.icon-only.md { min-width: 48px; height: 48px; }
                .btn.icon-only.lg { min-width: 56px; height: 56px; }
                .btn.icon-only.xs { min-width: 32px; height: 32px; }

                #loading-container, #icon-container {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                /* Variants */
                .btn.primary {
                    background: var(--accent);
                    color: black;
                    border-color: var(--accent);
                    --border-sketch: var(--accent);
                }
                .btn.primary::before {
                    border-color: var(--accent);
                    box-shadow: 2px 2px 0px rgba(0, 0, 0, 0.4);
                }
                .btn.primary::after {
                    border-color: var(--accent);
                    opacity: 0.4;
                }
                .btn.primary:hover:not(:disabled) {
                    transform: translate(-1px, -1px);
                }
                .btn.primary:hover:not(:disabled)::before {
                    box-shadow: 4px 4px 0px rgba(0, 0, 0, 0.3);
                }

                .btn.secondary {
                    background: var(--bg-card);
                    border-color: var(--border);
                    color: var(--text-main);
                    --border-sketch: var(--border);
                }
                .btn.secondary:hover:not(:disabled) {
                    border-color: var(--accent);
                    color: var(--accent);
                    --border-sketch: var(--accent);
                }

                .btn.white {
                    background: transparent;
                    border: 1.5px solid rgba(255, 255, 255, 0.9);
                    color: white;
                    --border-sketch: rgba(255, 255, 255, 0.8);
                }
                .btn.white:hover:not(:disabled) {
                    border-color: var(--accent);
                    color: var(--accent);
                    --border-sketch: var(--accent);
                    transform: translate(-1px, -1px);
                }

                .btn.success {
                    background: rgba(34, 197, 94, 0.1);
                    border-color: transparent;
                    color: #4ade80;
                    --border-sketch: transparent;
                }
                .btn.success:hover:not(:disabled) {
                    background: rgba(34, 197, 94, 0.2);
                    border-color: rgba(74, 222, 128, 0.2);
                    --border-sketch: rgba(74, 222, 128, 0.3);
                }

                .btn.danger {
                    background: rgba(239, 68, 68, 0.1);
                    border-color: rgba(239, 68, 68, 0.3);
                    color: #ef4444;
                    --border-sketch: rgba(239, 68, 68, 0.4);
                }
                .btn.danger::before {
                    border-color: rgba(239, 68, 68, 0.4);
                }
                .btn.danger:hover:not(:disabled) {
                    background: rgba(239, 68, 68, 0.2);
                    border-color: #ef4444;
                    --border-sketch: #ef4444;
                }

                .btn.ghost {
                    background: transparent;
                    color: var(--text-muted);
                    padding: 0.5rem;
                    border-color: transparent;
                    --border-sketch: transparent;
                }
                .btn.ghost:hover:not(:disabled) {
                    color: var(--text-main);
                    background: rgba(255, 255, 255, 0.05);
                }

                /* States */
                .btn:disabled, .btn.loading {
                    opacity: 0.5;
                    cursor: not-allowed;
                    transform: none !important;
                }
                .btn:disabled::before, .btn:disabled::after {
                    display: none;
                }

                .spinner {
                    width: 1.2em;
                    height: 1.2em;
                    border: 2px solid currentColor;
                    border-right-color: transparent;
                    border-radius: 50%;
                    animation: spin 0.75s linear infinite;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            </style>

            <button class="btn">
                <div id="loading-container"></div>
                <div id="icon-container"></div>
                <div id="content-container" style="position: relative; z-index: 2; display: flex; align-items: center; gap: 0.5rem;">
                   <slot></slot>
                </div>
            </button>
        `;
    }

    private updateRender() {
        if (!this.shadowRoot) return;
        const btn = this.shadowRoot.querySelector('.btn');
        if (!btn) return;

        const variant = this.getAttribute('variant') || 'primary';
        const size = this.getAttribute('size') || 'md';
        const icon = this.getAttribute('icon');
        const disabled = this.hasAttribute('disabled');
        const loading = this.hasAttribute('loading');
        const hasContent = !!this.innerHTML.trim();
        const type = this.getAttribute('type') || 'button';
        const title = this.getAttribute('title') || '';

        // Update classes and type without replacing the element
        btn.className = `btn ${variant} ${size} ${loading ? 'loading' : ''} ${(variant !== 'ghost' && variant !== 'success') ? 'sketch-simple' : ''} ${!hasContent ? 'icon-only' : ''}`;
        (btn as HTMLButtonElement).type = type as any;
        (btn as HTMLButtonElement).title = title;
        
        if (disabled) {
            btn.setAttribute('disabled', '');
        } else {
            btn.removeAttribute('disabled');
        }

        // Update loading/icon sub-elements
        const loadingContainer = this.shadowRoot.querySelector('#loading-container') as HTMLElement;
        if (loadingContainer) {
            loadingContainer.innerHTML = loading ? '<div class="spinner"></div>' : '';
            loadingContainer.style.display = loading ? 'flex' : 'none';
        }

        const iconContainer = this.shadowRoot.querySelector('#icon-container') as HTMLElement;
        if (iconContainer) {
            iconContainer.innerHTML = (icon && !loading) 
                ? `<app-icon name="${icon}" size="${(size === 'sm' || size === 'xs') ? 'sm' : 'md'}"></app-icon>` 
                : '';
            iconContainer.style.display = (icon && !loading) ? 'flex' : 'none';
        }

        const contentContainer = this.shadowRoot.querySelector('#content-container') as HTMLElement;
        if (contentContainer) {
            contentContainer.style.display = hasContent ? 'flex' : 'none';
        }
    }
}

customElements.define('app-button', AppButton);
